import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { to: "/", label: "Home" },
  { to: "/calculators", label: "Calculators" },
  { to: "/quiz", label: "Quiz" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const scrollToContact = (e) => {
    e?.preventDefault?.();
    if (location.pathname !== "/") {
      window.location.href = "/#contact";
      return;
    }
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-background/85 border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.brand}
          className="flex items-center gap-2.5 group"
        >
          <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center group-hover:scale-105 transition-transform">
            <Leaf className="w-4 h-4" />
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-lg font-semibold text-primary">Aarkays</span>
            <span className="block text-[10px] tracking-[0.18em] uppercase text-muted-foreground -mt-0.5">Consultancy</span>
          </span>
        </Link>

        <nav data-testid={NAV.links} className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-primary font-medium" : "text-foreground/70 hover:text-primary"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            onClick={scrollToContact}
            data-testid={NAV.cta}
            className="hidden md:inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform"
          >
            Talk to an Advisor
          </a>
          <button
            data-testid={NAV.mobileToggle}
            className="md:hidden p-2 rounded-full border border-border"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-2 text-base ${isActive ? "text-primary font-medium" : "text-foreground/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="#contact"
              onClick={scrollToContact}
              className="mt-2 inline-flex justify-center bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-medium"
            >
              Talk to an Advisor
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
